import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, ChevronDown, X } from "lucide-react";

/**
 * Interactive Full-Body Visual Scan
 *
 * Renders a SVG silhouette with 13 anatomical zones grouped under three regions
 * (Upper Extremity / Back-Torso / Lower Body). Player taps a zone, picks a pain
 * level, and the value flows up via `onChange(entries)`.
 *
 * Pain levels (ascending severity):
 *   tightness · soreness · discomfort · sharp_pain (CRITICAL — auto-shuts down)
 */
const PAIN_OPTIONS = [
  { id: "tightness",  label: "Tightness",   color: "#FCD34D" }, // amber-300
  { id: "soreness",   label: "Soreness",    color: "#FB923C" }, // orange-400
  { id: "discomfort", label: "Discomfort",  color: "#F87171" }, // red-400
  { id: "sharp_pain", label: "Sharp Pain",  color: "#DC2626" }, // red-600 — CRITICAL
];

const PAIN_COLOR = Object.fromEntries(PAIN_OPTIONS.map((p) => [p.id, p.color]));

export default function BodyScanMap({ value = [], onChange }) {
  const [zones, setZones] = useState([]);
  const [regions, setRegions] = useState([]);
  const [activeZone, setActiveZone] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    api.get("/body-scan/zones").then(({ data }) => {
      setZones(data.zones || []);
      setRegions(data.regions || []);
    });
  }, []);

  const entriesByZone = Object.fromEntries((value || []).map((e) => [e.zone_id, e.pain_level]));

  const setZonePain = (zone_id, pain_level) => {
    const others = (value || []).filter((e) => e.zone_id !== zone_id);
    const next = pain_level ? [...others, { zone_id, pain_level }] : others;
    onChange?.(next);
    setActiveZone(null);
  };

  const flaggedCount = (value || []).length;
  const sharpPainCount = (value || []).filter((e) => e.pain_level === "sharp_pain").length;

  return (
    <div className="rounded-xl ink-card p-4" data-testid="body-scan-map">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between"
        data-testid="body-scan-toggle"
      >
        <div className="text-left">
          <div className="font-display font-bold text-base text-white flex items-center gap-2">
            Full-Body Visual Scan
            {sharpPainCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase"
                style={{ backgroundColor: "rgba(220,38,38,0.18)", color: "#FCA5A5", border: "1px solid rgba(220,38,38,0.45)" }}>
                <AlertTriangle className="h-3 w-3" /> {sharpPainCount} sharp
              </span>
            )}
          </div>
          <div className="text-xs text-white/50 mt-0.5">
            Tap any zone to flag tightness, soreness, discomfort, or sharp pain.
            {flaggedCount > 0 && <span className="text-white/80"> · {flaggedCount} flagged</span>}
          </div>
        </div>
        <ChevronDown className={`h-5 w-5 text-white/50 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-4 grid md:grid-cols-[200px_1fr] gap-4 items-start">
          {/* Silhouette */}
          <div className="mx-auto md:mx-0 select-none">
            <Silhouette zones={zones} entriesByZone={entriesByZone} onPick={setActiveZone} />
            <div className="mt-2 flex flex-wrap justify-center gap-1.5 text-[10px]">
              {PAIN_OPTIONS.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 text-white/60">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} /> {p.label}
                </span>
              ))}
            </div>
          </div>

          {/* Region tabs + zone list */}
          <div className="space-y-3">
            {regions.map((region) => {
              const regionZones = zones.filter((z) => z.region === region.id);
              return (
                <div key={region.id} data-testid={`scan-region-${region.id}`}>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-1.5">{region.label}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {regionZones.map((z) => {
                      const pain = entriesByZone[z.id];
                      const isActive = activeZone === z.id;
                      const color = pain ? PAIN_COLOR[pain] : null;
                      return (
                        <button
                          type="button"
                          key={z.id}
                          onClick={() => setActiveZone((curr) => (curr === z.id ? null : z.id))}
                          className={`text-left px-2.5 py-2 rounded-lg border text-xs transition ${isActive ? "ring-2 ring-cyan-400" : ""}`}
                          style={{
                            backgroundColor: color ? `${color}1a` : "rgba(255,255,255,0.04)",
                            borderColor: color ? `${color}66` : "rgba(255,255,255,0.08)",
                          }}
                          data-testid={`scan-zone-${z.id}`}
                        >
                          <div className="font-semibold text-white">{z.label}</div>
                          {pain && (
                            <div className="text-[10px] mt-0.5 font-bold uppercase tracking-wide" style={{ color }}>
                              {pain.replace("_", " ")}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pain-level picker modal */}
      {activeZone && (() => {
        const z = zones.find((zz) => zz.id === activeZone);
        if (!z) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm px-4 pb-4"
            onClick={() => setActiveZone(null)} data-testid="scan-picker-overlay">
            <div className="w-full max-w-md rounded-2xl p-5 border border-[var(--ink-border-strong)]"
              style={{ backgroundColor: "var(--ink-2)" }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/40">{z.region}</div>
                  <h3 className="font-display font-black text-xl text-white mt-0.5">{z.label}</h3>
                  <p className="text-xs text-white/50 mt-1">How does this zone feel today?</p>
                </div>
                <button onClick={() => setActiveZone(null)} className="text-white/50 hover:text-white p-1"
                  data-testid="scan-picker-close"><X className="h-5 w-5" /></button>
              </div>
              <div className="mt-4 space-y-2">
                {PAIN_OPTIONS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setZonePain(z.id, p.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 hover:border-white/30 transition text-left bg-white/[0.02]"
                    style={{ borderLeft: `4px solid ${p.color}` }}
                    data-testid={`scan-pick-${p.id}`}
                  >
                    <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ backgroundColor: p.color }} />
                    <span className="flex-1 font-bold text-white">{p.label}</span>
                    {p.id === "sharp_pain" && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: "rgba(220,38,38,0.18)", color: "#FCA5A5" }}>
                        Forces Hybrid D
                      </span>
                    )}
                  </button>
                ))}
                {entriesByZone[z.id] && (
                  <button
                    type="button"
                    onClick={() => setZonePain(z.id, null)}
                    className="w-full px-4 py-2.5 rounded-xl text-sm text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
                    data-testid="scan-pick-clear"
                  >
                    Clear this zone
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/**
 * Minimalist front-view human silhouette with zone hit-targets.
 * viewBox: 240×520
 */
function Silhouette({ zones, entriesByZone, onPick }) {
  return (
    <svg viewBox="0 0 240 520" className="w-44 h-auto block" aria-label="Body map">
      {/* Body outline */}
      <g fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2">
        {/* Head */}
        <circle cx="120" cy="48" r="28" />
        {/* Neck */}
        <path d="M108 76 L108 92 L132 92 L132 76" />
        {/* Torso */}
        <path d="M70 100 L170 100 L182 200 L168 295 L72 295 L58 200 Z" />
        {/* Arms */}
        <path d="M70 100 L40 200 L46 260 L60 260 L72 200" />
        <path d="M170 100 L200 200 L194 260 L180 260 L168 200" />
        {/* Legs */}
        <path d="M88 295 L78 420 L84 500 L106 500 L112 420 L116 305" />
        <path d="M152 295 L162 420 L156 500 L134 500 L128 420 L124 305" />
      </g>
      {/* Zone hit-targets */}
      {zones.map((z) => {
        const pain = entriesByZone[z.id];
        const color = pain ? PAIN_COLOR[pain] : "rgba(34,211,238,0.6)";
        const r = pain ? 11 : 7;
        return (
          <g key={z.id} style={{ cursor: "pointer" }} onClick={() => onPick(z.id)} data-testid={`silhouette-zone-${z.id}`}>
            <circle cx={z.coords.x} cy={z.coords.y} r={r + 6} fill="transparent" />
            <circle
              cx={z.coords.x}
              cy={z.coords.y}
              r={r}
              fill={pain ? color : "rgba(34,211,238,0.18)"}
              stroke={pain ? color : "rgba(34,211,238,0.7)"}
              strokeWidth="1.5"
            >
              {pain === "sharp_pain" && (
                <animate attributeName="opacity" values="1;0.5;1" dur="1.4s" repeatCount="indefinite" />
              )}
            </circle>
          </g>
        );
      })}
    </svg>
  );
}
